import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import { 
  History, Loader2, CheckCircle2, Clock, AlertCircle, 
  Receipt, Download, Eye, FileImage, Calendar, DollarSign,
  TrendingUp, TrendingDown, Upload, X, Image as ImageIcon
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const categoryConfig = {
  accommodation: { label: 'Hospedagem', icon: '🏨', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
  transport: { label: 'Transporte', icon: '🚗', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  food: { label: 'Alimentação', icon: '🍽️', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
  activities: { label: 'Atividades', icon: '🎯', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' },
  shopping: { label: 'Compras', icon: '🛍️', color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400' },
  other: { label: 'Outros', icon: '📦', color: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300' },
};

export function PaymentHistoryModal({ 
  tripId, 
  memberId, 
  memberName, 
  memberAvatar,
  isOrganizer,
  trigger 
}) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedProof, setSelectedProof] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/payments/member/${memberId}/history`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, tripId, memberId]);

  const getProofUrl = (proofId) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}/api/trips/${tripId}/files/${proofId}/view?t=${Date.now()}`;
  };

  const filteredHistory = data?.history?.filter(item => {
    if (activeTab === 'all') return true;
    if (activeTab === 'paid') return item.is_paid;
    if (activeTab === 'pending') return !item.is_paid;
    return true;
  }) || [];

  const paidCount = data?.paid_count || 0;
  const totalCount = data?.payments_count || 0;
  const progressPercent = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="ghost" size="sm" data-testid={`payment-history-btn-${memberId}`}>
              <History className="w-4 h-4 mr-1" />
              Histórico
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="w-10 h-10">
                <AvatarFallback className="bg-orange-100 text-lg">
                  {memberAvatar || memberName?.charAt(0) || '👤'}
                </AvatarFallback>
              </Avatar>
              <div>
                <span className="block">Histórico de Pagamentos</span>
                <span className="text-sm font-normal text-slate-500 dark:text-slate-400">{data?.member_name || memberName}</span>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : data ? (
            <div className="flex-1 overflow-y-auto space-y-4 mt-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-slate-100 dark:border-slate-700">
                  <CardContent className="p-3 text-center">
                    <DollarSign className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total Devido</p>
                    <p className="font-bold text-slate-900 dark:text-white">
                      R$ {data.total_due.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-green-100 bg-green-50/50">
                  <CardContent className="p-3 text-center">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total Pago</p>
                    <p className="font-bold text-green-600">
                      R$ {data.total_paid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </CardContent>
                </Card>
                <Card className={`border-slate-100 ${data.balance < 0 ? 'bg-yellow-50/50' : 'bg-green-50/50'}`}>
                  <CardContent className="p-3 text-center">
                    {data.balance < 0 ? (
                      <TrendingDown className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                    ) : (
                      <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400">Saldo</p>
                    <p className={`font-bold ${data.balance < 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                      R$ {Math.abs(data.balance).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      {data.balance < 0 ? ' (deve)' : ''}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Progress */}
              <div className="px-1">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-600 dark:text-slate-400">Progresso</span>
                  <span className="text-slate-500 dark:text-slate-400">{paidCount} de {totalCount} pagos</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="all" className="flex-1">
                    Todos ({totalCount})
                  </TabsTrigger>
                  <TabsTrigger value="paid" className="flex-1">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Pagos ({paidCount})
                  </TabsTrigger>
                  <TabsTrigger value="pending" className="flex-1">
                    <Clock className="w-3 h-3 mr-1" />
                    Pendentes ({totalCount - paidCount})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-4 space-y-3">
                  {filteredHistory.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <Receipt className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>Nenhum pagamento encontrado</p>
                    </div>
                  ) : (
                    filteredHistory.map((item, index) => {
                      const category = categoryConfig[item.payment_category] || categoryConfig.other;
                      return (
                        <Card key={`${item.payment_id}-${index}`} className="border-slate-100 dark:border-slate-700">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center text-lg flex-shrink-0`}>
                                  {category.icon}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-medium text-slate-900 dark:text-white truncate">
                                      {item.payment_title}
                                    </h4>
                                    {item.is_paid ? (
                                      <Badge className="bg-green-100 text-green-700 text-xs">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Pago
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                                        <Clock className="w-3 h-3 mr-1" />
                                        Pendente
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    {item.payment_due_date && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        Venc: {format(parseISO(item.payment_due_date), "dd/MM/yy", { locale: ptBR })}
                                      </span>
                                    )}
                                    {item.paid_at && (
                                      <span className="flex items-center gap-1 text-green-600">
                                        <CheckCircle2 className="w-3 h-3" />
                                        Pago em {format(parseISO(item.paid_at), "dd/MM/yy", { locale: ptBR })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="text-right flex-shrink-0 ml-2">
                                <p className="font-bold text-slate-900 dark:text-white">
                                  R$ {item.amount_due.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </p>
                                {item.amount_paid > 0 && item.amount_paid < item.amount_due && (
                                  <p className="text-xs text-green-600">
                                    Pago: R$ {item.amount_paid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Proof Section */}
                            {item.proof_url && (
                              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-slate-500 flex items-center gap-1">
                                    <Receipt className="w-3 h-3" />
                                    Comprovante anexado
                                  </span>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => setSelectedProof({
                                        url: getProofUrl(item.proof_url),
                                        title: item.payment_title,
                                        filename: item.proof_file?.filename
                                      })}
                                      data-testid={`view-proof-${item.payment_id}`}
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      Ver
                                    </Button>
                                    <a
                                      href={getProofUrl(item.proof_url)}
                                      download={item.proof_file?.filename || 'comprovante'}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <Button variant="outline" size="sm" className="h-7 text-xs">
                                        <Download className="w-3 h-3 mr-1" />
                                        Baixar
                                      </Button>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* No proof indicator for paid items */}
                            {item.is_paid && !item.proof_url && (
                              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  Sem comprovante anexado
                                </span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              <AlertCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Erro ao carregar histórico</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Proof Viewer Modal */}
      <Dialog open={!!selectedProof} onOpenChange={() => setSelectedProof(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-orange-500" />
              Comprovante - {selectedProof?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {selectedProof && (
              <div className="bg-slate-100 rounded-lg p-2 flex items-center justify-center min-h-[300px]">
                <img
                  src={selectedProof.url}
                  alt="Comprovante"
                  className="max-w-full max-h-[60vh] object-contain rounded"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.parentElement.innerHTML = `
                      <div class="text-center py-12">
                        <p class="text-slate-500 dark:text-slate-400">Não foi possível carregar a imagem</p>
                        <a href="${selectedProof.url}" target="_blank" class="text-orange-500 hover:underline mt-2 inline-block">
                          Abrir em nova aba
                        </a>
                      </div>
                    `;
                  }}
                />
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setSelectedProof(null)}>
                Fechar
              </Button>
              {selectedProof && (
                <a href={selectedProof.url} download target="_blank" rel="noopener noreferrer">
                  <Button className="bg-orange-500 hover:bg-orange-600">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar
                  </Button>
                </a>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PaymentHistoryModal;
